/* eslint-disable @typescript-eslint/no-explicit-any */
import { Game } from 'phaser';
import type { GameState } from 'hearts-engine';
import { GameEvent } from 'hearts-engine';
import { EventEmitter } from 'hearts-engine';

import GameScene from '@/GameUI/Scenes/GameScene';
import { MAX_HEIGHT, MAX_WIDTH } from '@/utilities/Screen';
import type { TFunction } from '@/GameUI/Definitions';
import type { GameEngine } from '@/index';

export interface GameOptions {
	debug?: boolean;
	localPlayer?: number;
	allBots?: boolean;
	audioEnabled?: boolean;
	translate?: TFunction;
	gameManager: GameEngine.Manager;
}

const defaultTranslate = (str: string) => str;

function getOptionDefaults(options: GameOptions): GameOptions {
	const { debug, localPlayer, allBots, translate } = options;
	return {
		...options,
		debug: debug ?? false,
		localPlayer: localPlayer ?? 2,
		allBots: allBots ?? false,
		translate: translate ?? (defaultTranslate as TFunction),
	};
}

export default class GameUI extends EventEmitter {
	game: Game = null;
	gameScene: GameScene = null;
	gameWidth: number = MAX_WIDTH;
	gameHeight: number = MAX_HEIGHT;
	parent: string = null;
	maxWidth: number = 1440;
	maxHeight: number = 1080;
	quality: string = 'ultra';
	options: GameOptions;

	constructor(options: GameOptions) {
		super();

		this.options = getOptionDefaults(options);

		if (!this.options.gameManager) {
			throw new Error('Game Manager not provided');
		}

		if (this.options.debug) {
			console.info('manager is', this.options.gameManager);
		}

		this.gameScene = new GameScene(this.options);
	}

	create(parentElementId?: string, options?: Phaser.Types.Core.GameConfig | null) {

		parentElementId = parentElementId ?? 'game';

		const parentElement = document.getElementById(parentElementId) as HTMLCanvasElement;

		const width = 800; //1080
		const height = 800;//1620;

		this.parent = parentElementId;

		options = {
			banner: false,
			width: this.gameWidth,
			height: this.gameHeight,
			parent: parentElement,
			scale: {
				mode: Phaser.Scale.NONE, // we will resize the game with our own code (see Boot.js)
				width: width, // set game width by multiplying window width with devicePixelRatio
				height: height, // set game height by multiplying window height with devicePixelRatio
				autoRound: true,
			},
			...options,
		};

		this.game = new Game(options);
		this.game.scene.add('scene-game', this.gameScene);
		this.game.scene.start('scene-game');

		const resizeListener = () => {
			if (this.options.debug) {
				console.log('resize');
			}

			this.resize();
		};

		window.addEventListener('resize', resizeListener);
		const resizeObserver = new ResizeObserver(resizeListener);
		if (parentElement) {
			resizeObserver.observe(parentElement);
		}
		this.game.events.on('destroy', () => {
			window.removeEventListener('resize', resizeListener);
			resizeObserver.disconnect();
		});
		this.game.events.on(GameEvent.StateChanged, (state: GameState) => {
			this.emit(GameEvent.StateChanged, state);
		});
		this.resize();
	}

	startGame() {
		this.gameScene.handleStart();
	}
	// resize() {
	// 	//add more width or height to the game to match the aspect ratio of the screen and let phaser stretch with mode: Phaser.Scale.FIT

	// 	const width = this.game.scale.parentSize.width;
	// 	const height = this.game.scale.parentSize.height;
	// 	const aspectRatioParent = width / height;
	// 	const aspectRatioGame = MAX_WIDTH / MAX_HEIGHT;

	// 	if (aspectRatioParent > aspectRatioGame) {
	// 		const newWidth = MAX_HEIGHT * aspectRatioParent;
	// 		this.game.scale.setGameSize(newWidth, MAX_HEIGHT);
	// 	}
	// 	else {
	// 		const newHeight = MAX_WIDTH / aspectRatioParent;
	// 		this.game.scale.setGameSize(MAX_WIDTH, newHeight);
	// 	}
	// }

	//resize to fit screen, but not larger than max width and height
	resize(quality?: any) {
		if (!this.game || !this.game.scale) {
			return;
		}

		const canvasParent = document.getElementById(this.parent);

		const gameSize = this.getGameSize(quality, canvasParent);

		// Set the drawing buffer first, then size the CSS box to fit the parent.
		// Order matters: Scale.NONE's setGameSize can overwrite canvas style, so we apply ours after.
		this.game.scale.setGameSize(gameSize.width, gameSize.height);

		const canvasSize = this.getCanvasSize(gameSize.width, gameSize.height, canvasParent);
		const canvas = this.game.canvas;
		canvas.style.width = canvasSize.width + 'px';
		canvas.style.height = canvasSize.height + 'px';
		canvas.style.marginLeft = canvasSize.marginLeft + 'px';
		canvas.style.marginTop = canvasSize.marginTop + 'px';
		this.game.scale.refresh();

		if (this.options.debug) {
			console.log('resize', gameSize);
		}
	}

	getGameSize(quality: string, parent: any, devicePixelRatio?: number) {
		//scale the game size (canvas pixels) to increase quailty or performance based on screen size.
		if (!parent) {
			parent = window.screen;
		}

		if (!devicePixelRatio) {
			devicePixelRatio = window.devicePixelRatio || 1;
		}

		const parentWidth = (parent.innerWidth || parent.clientWidth || parent.width);
		const parentHeight = (parent.innerHeight || parent.clientHeight || parent.height);
		let maxWidth = this.maxWidth;
		//let maxHeight = this.maxHeight;

		const screenWidth = Math.round(parentWidth * devicePixelRatio);
		const screenHeight = Math.round(parentHeight * devicePixelRatio);

		if (!quality) {
			quality = this.quality;
		}

		if (quality == 'low') {
			maxWidth = 360;
		}

		if (quality == 'medium') {
			maxWidth = 500;
		}

		if (quality == 'high') {
			maxWidth = 720;
		}

		if (quality == 'ultra') {
			maxWidth = this.maxWidth;
		}
		//let qualityScale = 1.0;

		const divisor = Math.max(1, Math.ceil(Math.min(screenWidth, screenHeight) / maxWidth));

		return {
			width: Math.round(screenWidth / divisor),
			height: Math.round(screenHeight / divisor),
		};

	}

	getCanvasSize(width: number, height: number, parent: any) {
		//size the canvas (canvas width and height) to fit to screen and center
		if (!parent) {
			parent = window;
		}

		const parentWidth = (parent.innerWidth || parent.clientWidth || parent.width);
		const parentHeight = (parent.innerHeight || parent.clientHeight || parent.height);
		const maxWidth = parentWidth;
		const maxHeight = parentHeight;
		const scaleX = maxWidth / width;
		const scaleY = maxHeight / height;
		const scale = Math.min(scaleX, scaleY);
		const newWidth = width * scale;
		const newHeight = height * scale;
		const marginLeft = (maxWidth - newWidth) / 2;
		const marginTop = (maxHeight - newHeight) / 2;

		return {
			width: Math.round(newWidth),
			height: Math.round(newHeight),
			marginLeft: Math.round(marginLeft),
			marginTop: Math.round(marginTop),
		};
	}

	setAudioEnabled(audioEnabled: boolean) {
		this.gameScene.updateOptions({
			audioEnabled,
		});
	}
}
